import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import AuthContext from "@/context/AuthContextInstance";
import { Shield, User, Lock, Eye } from "lucide-react";

const API_URL = import.meta.env.VITE_API_URL;

const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes in ms

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [userType, setUserType] = useState<"staff" | "admin">("staff");
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user, setUser } = useContext(AuthContext);

  useEffect(() => {
    if (user) {
      navigate(user.role === "admin" ? "/admin" : "/staff", { replace: true });
    }
  }, [user, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: "Login Successful",
          description: `Welcome ${
            data.user.role === "admin" ? "Administrator" : "Staff Member"
          }!`,
        });
        // Save user details from login response to AuthContext
        setUser({
          id: data.user.id,
          name:
            data.user.name ||
            (data.user.role === "admin" ? "Administrator" : "Staff Member"),
          role: data.user.role,
        });
        // console.log("USER:(Login)", data.user);
        // Call /me to update AuthContext again for session validation
        try {
          const meRes = await fetch(`${API_URL}/api/auth/me`, {
            credentials: "include",
          });
          if (meRes.ok) {
            const meData = await meRes.json();
            setUser({
              id: meData.user.id,
              name:
                meData.user.name ||
                (meData.user.role === "admin"
                  ? "Administrator"
                  : "Staff Member"),
              role: meData.user.role,
            });
            // console.log("USER (me):", meData.user);
          }
        } catch (e) {
          // Ignore errors, navigation will still work
        }
        navigate(data.user.role === "admin" ? "/admin" : "/staff", {
          replace: true,
        });
      } else {
        toast({
          title: "Login Failed",
          description:
            data.error || "Invalid email or password. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Login Failed",
        description: "Server error. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary via-primary-hover to-foreground flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-card rounded-2xl shadow-xl mb-4">
            <Shield className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            Vehicle Registration System
          </h1>
          <p className="text-blue-100">
            Secure access for authorized personnel
          </p>
        </div>

        <Card className="shadow-2xl border-0 backdrop-blur-sm bg-card/95">
          <CardHeader className="text-center pb-4">
            <CardTitle className="text-2xl">Sign In</CardTitle>
            <CardDescription>
              Enter your credentials to access the system
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-6">
              <div className="flex gap-2 mb-6">
                <Badge
                  variant={userType === "staff" ? "default" : "outline"}
                  className="flex-1 justify-center py-2 cursor-pointer transition-all"
                  onClick={() => setUserType("staff")}
                >
                  <User className="w-4 h-4 mr-1" />
                  Staff
                </Badge>
                <Badge
                  variant={userType === "admin" ? "default" : "outline"}
                  className="flex-1 justify-center py-2 cursor-pointer transition-all"
                  onClick={() => setUserType("admin")}
                >
                  <Shield className="w-4 h-4 mr-1" />
                  Admin
                </Badge>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-12"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-12 pr-10"
                  />
                  <button
                    type="button"
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary focus:outline-none"
                    tabIndex={0}
                  >
                    {showPassword ? (
                      <Lock className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 text-base"
                variant="gradient"
              >
                <Lock className="w-4 h-4 mr-2" />
                Sign In as{" "}
                {userType === "admin" ? "Administrator" : "Staff Member"}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm text-muted-foreground">
              <p>Authorized access only</p>
              <p>Contact your administrator for support</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;
